import { existsSync, readFileSync, statSync } from "node:fs";
import { dirname, join, resolve, sep } from "node:path";
import chalk from "chalk";
import { CONFIG_DIR_NAME, getBundledExtensionsDir, getBundledPromptsDir, getBundledSkillsDir } from "../config.ts";
import { loadThemeFromPath, type Theme } from "../modes/interactive/theme/theme.ts";
import type { ResourceDiagnostic } from "./diagnostics.ts";

export type { ResourceCollision, ResourceDiagnostic } from "./diagnostics.ts";

import { canonicalizePath, isLocalPath, resolvePath } from "../utils/paths.ts";
import { configFile, resourceDir } from "./agent-paths.ts";
import { createEventBus, type EventBus } from "./event-bus.ts";
import {
	hasProfileExtensionImportAuthority,
	isDefaultOnBundledExtension,
	isExtensionPathAllowedForImport,
} from "./extension-import-authority.ts";
import {
	createExtensionRuntime,
	disposeExtensionEventSubscriptions,
	loadExtension,
	loadExtensionFromFactory,
	loadExtensions,
} from "./extensions/loader.ts";
import type { Extension, ExtensionFactory, ExtensionRuntime, LoadExtensionsResult } from "./extensions/types.ts";
import { DefaultPackageManager, type PathMetadata } from "./package-manager.ts";
import type { PromptTemplate } from "./prompt-templates.ts";
import { loadPromptTemplates } from "./prompt-templates.ts";
import {
	mergeResourceProfileMap,
	parseResourceProfileBlocks,
	stripResourceProfileBlocks,
} from "./resource-profile-blocks.ts";
import { collectResourceFilesRecursively, isResourcePathWithin, readResourceDirectory } from "./resource-traversal.ts";
import { scanContextFileThreats, stripInvisibleUnicode } from "./security/context-threat-scanner.ts";
import {
	matchesResourceProfilePattern,
	type ResourceProfileKind,
	type ResourceProfileSettings,
	SettingsManager,
} from "./settings-manager.ts";
import type { Skill } from "./skills.ts";
import { loadSkills } from "./skills.ts";
import { createSourceInfo, type SourceInfo } from "./source-info.ts";

export {
	hasInvisibleUnicode,
	scanContextFileThreats,
	stripInvisibleUnicode,
	type ThreatScope,
} from "./security/context-threat-scanner.ts";

export interface ResourceExtensionPaths {
	skillPaths?: Array<{ path: string; metadata: PathMetadata }>;
	promptPaths?: Array<{ path: string; metadata: PathMetadata }>;
	themePaths?: Array<{ path: string; metadata: PathMetadata }>;
}

export interface ResourceReloadOptions {
	/** Throw instead of accepting a hot reload that produced extension load/conflict errors. */
	failOnExtensionErrors?: boolean;
	/** Keep the previous extension generation alive until commitReload()/rollbackReload(). */
	deferExtensionDispose?: boolean;
	/** The caller already loaded the settings generation this resource pass must use. */
	skipSettingsReload?: boolean;
}

export interface ResourceLoader {
	getExtensions(): LoadExtensionsResult;
	getSkills(): { skills: Skill[]; diagnostics: ResourceDiagnostic[] };
	/** Skills allowed by the currently active resource profile — use for selection/invocation/agent-visibility. */
	getActiveSkills(): Skill[];
	getPrompts(): { prompts: PromptTemplate[]; diagnostics: ResourceDiagnostic[] };
	/** Prompt templates allowed by the currently active resource profile — use for selection/invocation/agent-visibility. */
	getActivePrompts(): PromptTemplate[];
	getThemes(): { themes: Theme[]; diagnostics: ResourceDiagnostic[] };
	/** Themes allowed by the currently active resource profile. */
	getActiveThemes(): Theme[];
	getAgentsFiles(): { agentsFiles: Array<{ path: string; content?: string }> };
	/** Warnings about context files withheld by the active profile (empty when none). */
	getAgentsDiagnostics(): ResourceDiagnostic[];
	/** Profile-INDEPENDENT discovery (editor universe; metadata only, never loads content). */
	getDiscoverableSkillPaths(): string[];
	getDiscoverablePromptPaths(): string[];
	getDiscoverableAgentsFilePaths(): string[];
	getSystemPrompt(): string | undefined;
	getAppendSystemPrompt(): string[];
	getLoadedExtension(path: string): Extension | undefined;
	removeLoadedExtension(path: string): Extension | undefined;
	loadSingleExtension(path: string): Promise<{ extension: Extension | null; error: string | null }>;
	/** Load one draft against a fresh runtime without mutating the live extension generation. */
	loadIsolatedExtension?(path: string, cwd: string): Promise<{ extension: Extension | null; error: string | null }>;
	extendResources(paths: ResourceExtensionPaths): void;
	reload(options?: ResourceReloadOptions): Promise<void>;
	/**
	 * Re-scan skill files so a just-written or archived skill is selectable this session.
	 * Full `/reload` is not required. Optional on custom loaders.
	 */
	refreshSkills?(): void;
	commitReload?(): void | Promise<void>;
	rollbackReload?(): void | Promise<void>;
	/** Release the current (and any deferred) extension generation. Session dispose must call this. */
	dispose?(): void | Promise<void>;
	/** Get all discoverable extension paths (enabled and disabled) */
	getDiscoverableExtensionPaths(): Promise<string[]>;
}

interface ResourceLoaderSnapshot {
	extensionsResult: LoadExtensionsResult;
	skills: Skill[];
	skillDiagnostics: ResourceDiagnostic[];
	prompts: PromptTemplate[];
	promptDiagnostics: ResourceDiagnostic[];
	themes: Theme[];
	themeDiagnostics: ResourceDiagnostic[];
	agentsFiles: Array<{ path: string; content?: string }>;
	agentsDiagnostics: ResourceDiagnostic[];
	lastAgentsFilePaths: string[];
	discoverableSkillPaths: string[];
	discoverablePromptPaths: string[];
	systemPrompt?: string;
	appendSystemPrompt: string[];
	lastSkillPaths: string[];
	extensionSkillSourceInfos: Map<string, SourceInfo>;
	extensionPromptSourceInfos: Map<string, SourceInfo>;
	extensionThemeSourceInfos: Map<string, SourceInfo>;
	lastPromptPaths: string[];
	lastThemePaths: string[];
}

function resolvePromptInput(input: string | undefined, description: string): string | undefined {
	if (!input) {
		return undefined;
	}

	if (existsSync(input)) {
		try {
			return readFileSync(input, "utf-8");
		} catch (error) {
			console.error(chalk.yellow(`Warning: Could not read ${description} file ${input}: ${error}`));
			return input;
		}
	}

	return input;
}

/**
 * Genuinely-dangerous invisible / bidi-control characters used to HIDE instructions or visually reorder
 * text (Trojan-Source): zero-width space (U+200B), bidi embeddings/overrides (U+202A\u2013U+202E), word-joiner
 * + invisible math operators (U+2060\u2013U+2064), bidi isolates + deprecated format controls (U+2066\u2013U+206F),
 * and BOM/zero-width-no-break (U+FEFF).
 *
 * DELIBERATELY EXCLUDES U+200C ZWNJ, U+200D ZWJ, U+200E LRM, U+200F RLM \u2014 these are LEGITIMATE and
 * load-bearing in Persian/Arabic/Hebrew/Hindi shaping (joiner control, directionality) and in emoji ZWJ
 * sequences; stripping them corrupts real text (bug #35). The Trojan-Source reorder attack relies on the
 * embeddings/overrides/isolates above, which are still stripped. (Hermes-parity #31/#35.)
 */
function sanitizeContextFileContent(filePath: string, content: string): string {
	const profileFreeContent = stripResourceProfileBlocks(content);
	// Strip-and-continue for hidden/bidi-control chars: don't reject a whole file for benign zero-width
	// chars in legitimate international text, but neutralize any payload hidden with them (agy #31).
	const { cleaned, removed } = stripInvisibleUnicode(profileFreeContent);
	if (removed > 0) {
		console.error(chalk.yellow(`Warning: stripped ${removed} invisible/bidi char(s) from ${filePath}`));
	}
	const findings = scanContextFileThreats(cleaned);
	if (findings.length === 0) return cleaned;
	console.error(chalk.yellow(`Warning: Blocked context file ${filePath}: ${findings.join(", ")}`));
	return `[BLOCKED: ${filePath} contained potential prompt injection (${findings.join(", ")}). Content not loaded.]`;
}

/**
 * RAW (unsanitized) context files from `dir`. Sanitization/threat-scanning is DEFERRED to the caller
 * so a profile-DENIED file's content is never processed into the session — only its embedded
 * `<resource-profile>` blocks are read for discovery (see {@link loadRawProjectContextFiles}).
 */
export function isGlobalAgentsFile(
	filePath: string,
	agentDir: string,
	platform: NodeJS.Platform = process.platform,
): boolean {
	const fileDir = canonicalizePath(dirname(filePath));
	const resolvedAgentDir = canonicalizePath(agentDir);
	return platform === "win32"
		? fileDir.toLowerCase() === resolvedAgentDir.toLowerCase()
		: fileDir === resolvedAgentDir;
}

function loadRawContextFilesFromDir(dir: string): Array<{ path: string; rawContent: string }> {
	const candidates = ["AGENTS.md", "AGENTS.MD", "CLAUDE.md", "CLAUDE.MD", "GEMINI.md", "GEMINI.MD"];
	const files: Array<{ path: string; rawContent: string }> = [];
	for (const filename of candidates) {
		const filePath = join(dir, filename);
		if (existsSync(filePath)) {
			try {
				if (!statSync(filePath).isFile()) continue;
				files.push({ path: filePath, rawContent: readFileSync(filePath, "utf-8") });
			} catch (error) {
				console.error(chalk.yellow(`Warning: Could not read ${filePath}: ${error}`));
			}
		}
	}
	return files;
}

/**
 * Discover project/agent context files with their RAW content (profile blocks intact, no sanitize).
 * The agents-kind profile filter can be DEFINED inside these same files (embedded `<resource-profile>`
 * blocks), so callers must read raw to discover profiles before knowing which files the filter denies;
 * sanitization/exposure is then applied only to files the filter allows.
 */
export function loadRawProjectContextFiles(options: {
	cwd: string;
	agentDir: string;
	projectTrusted?: boolean;
	/** When true, walk cwd/ancestor context files. Default is global `agentDir` files only. */
	includeProject?: boolean;
	/** Testable filesystem case semantics; production defaults to the current platform. */
	platform?: NodeJS.Platform;
}): Array<{ path: string; rawContent: string }> {
	const resolvedCwd = resolvePath(options.cwd);
	const resolvedAgentDir = resolvePath(options.agentDir);
	const platform = options.platform ?? process.platform;
	const pathIdentity = (filePath: string): string => {
		const canonicalPath = canonicalizePath(filePath);
		return platform === "win32" ? canonicalPath.toLowerCase() : canonicalPath;
	};

	const contextFiles: Array<{ path: string; rawContent: string }> = [];
	const seenPathIdentities = new Set<string>();

	for (const globalContext of loadRawContextFilesFromDir(resolvedAgentDir)) {
		const identity = pathIdentity(globalContext.path);
		if (seenPathIdentities.has(identity)) continue;
		seenPathIdentities.add(identity);
		contextFiles.push(globalContext);
	}

	if (options.includeProject === true && options.projectTrusted !== false) {
		const ancestorContextFiles: Array<{ path: string; rawContent: string }> = [];

		let currentDir = resolvedCwd;
		const root = resolve("/");

		while (true) {
			const contextFilesInDir: Array<{ path: string; rawContent: string }> = [];
			for (const contextFile of loadRawContextFilesFromDir(currentDir)) {
				const identity = pathIdentity(contextFile.path);
				if (seenPathIdentities.has(identity)) continue;
				seenPathIdentities.add(identity);
				contextFilesInDir.push(contextFile);
			}
			if (contextFilesInDir.length > 0) {
				ancestorContextFiles.unshift(...contextFilesInDir);
			}

			if (currentDir === root) break;

			const parentDir = resolve(currentDir, "..");
			if (parentDir === currentDir) break;
			currentDir = parentDir;
		}

		contextFiles.push(...ancestorContextFiles);
	}

	return contextFiles;
}

/**
 * Discover project/agent context files with SANITIZED content (profile blocks stripped, invisible/bidi
 * chars removed, prompt-injection scanned). Every discovered file is sanitized — callers that must
 * respect a profile's agents-kind denial should use {@link loadRawProjectContextFiles} and sanitize
 * only the allowed subset instead, so denied content is never processed.
 */
export function loadProjectContextFiles(options: {
	cwd: string;
	agentDir: string;
	projectTrusted?: boolean;
	/** When true, list cwd/ancestor files as paths. Default is global `agentDir` files only. */
	includeProject?: boolean;
	platform?: NodeJS.Platform;
}): Array<{ path: string; content?: string }> {
	return loadRawProjectContextFiles({
		cwd: options.cwd,
		agentDir: options.agentDir,
		projectTrusted: options.projectTrusted,
		includeProject: options.includeProject === true,
		platform: options.platform,
	}).map((file) =>
		isGlobalAgentsFile(file.path, options.agentDir, options.platform)
			? { path: file.path, content: sanitizeContextFileContent(file.path, file.rawContent) }
			: { path: file.path },
	);
}

export interface DefaultResourceLoaderOptions {
	cwd: string;
	agentDir: string;
	settingsManager?: SettingsManager;
	eventBus?: EventBus;
	additionalExtensionPaths?: string[];
	additionalSkillPaths?: string[];
	additionalPromptTemplatePaths?: string[];
	additionalThemePaths?: string[];
	extensionFactories?: ExtensionFactory[];
	noExtensions?: boolean;
	noSkills?: boolean;
	noPromptTemplates?: boolean;
	noThemes?: boolean;
	noContextFiles?: boolean;
	systemPrompt?: string;
	appendSystemPrompt?: string[];
	extensionsOverride?: (base: LoadExtensionsResult) => LoadExtensionsResult;
	skillsOverride?: (base: { skills: Skill[]; diagnostics: ResourceDiagnostic[] }) => {
		skills: Skill[];
		diagnostics: ResourceDiagnostic[];
	};
	promptsOverride?: (base: { prompts: PromptTemplate[]; diagnostics: ResourceDiagnostic[] }) => {
		prompts: PromptTemplate[];
		diagnostics: ResourceDiagnostic[];
	};
	themesOverride?: (base: { themes: Theme[]; diagnostics: ResourceDiagnostic[] }) => {
		themes: Theme[];
		diagnostics: ResourceDiagnostic[];
	};
	agentsFilesOverride?: (base: { agentsFiles: Array<{ path: string; content?: string }> }) => {
		agentsFiles: Array<{ path: string; content?: string }>;
	};
	systemPromptOverride?: (base: string | undefined) => string | undefined;
	appendSystemPromptOverride?: (base: string[]) => string[];
}

export class DefaultResourceLoader implements ResourceLoader {
	private cwd: string;
	private agentDir: string;
	private settingsManager: SettingsManager;
	private eventBus: EventBus;
	private packageManager: DefaultPackageManager;
	private additionalExtensionPaths: string[];
	private additionalSkillPaths: string[];
	private additionalPromptTemplatePaths: string[];
	private additionalThemePaths: string[];
	private extensionFactories: ExtensionFactory[];
	private noExtensions: boolean;
	private noSkills: boolean;
	private noPromptTemplates: boolean;
	private noThemes: boolean;
	private noContextFiles: boolean;
	private systemPromptSource?: string;
	private appendSystemPromptSource?: string[];
	private extensionsOverride?: (base: LoadExtensionsResult) => LoadExtensionsResult;
	private skillsOverride?: (base: { skills: Skill[]; diagnostics: ResourceDiagnostic[] }) => {
		skills: Skill[];
		diagnostics: ResourceDiagnostic[];
	};
	private promptsOverride?: (base: { prompts: PromptTemplate[]; diagnostics: ResourceDiagnostic[] }) => {
		prompts: PromptTemplate[];
		diagnostics: ResourceDiagnostic[];
	};
	private themesOverride?: (base: { themes: Theme[]; diagnostics: ResourceDiagnostic[] }) => {
		themes: Theme[];
		diagnostics: ResourceDiagnostic[];
	};
	private agentsFilesOverride?: (base: { agentsFiles: Array<{ path: string; content?: string }> }) => {
		agentsFiles: Array<{ path: string; content?: string }>;
	};
	private systemPromptOverride?: (base: string | undefined) => string | undefined;
	private appendSystemPromptOverride?: (base: string[]) => string[];

	private extensionsResult: LoadExtensionsResult;
	private skills: Skill[];
	private skillDiagnostics: ResourceDiagnostic[];
	private prompts: PromptTemplate[];
	private promptDiagnostics: ResourceDiagnostic[];
	private themes: Theme[];
	private themeDiagnostics: ResourceDiagnostic[];
	private agentsFiles: Array<{ path: string; content?: string }>;
	private systemPrompt?: string;
	private appendSystemPrompt: string[];
	private lastSkillPaths: string[];
	private lastAgentsFilePaths: string[] = [];
	private discoverableSkillPaths: string[] = [];
	private discoverablePromptPaths: string[] = [];
	private agentsDiagnostics: ResourceDiagnostic[] = [];
	private extensionSkillSourceInfos: Map<string, SourceInfo>;
	private extensionPromptSourceInfos: Map<string, SourceInfo>;
	private extensionThemeSourceInfos: Map<string, SourceInfo>;
	private lastPromptPaths: string[];
	private lastThemePaths: string[];
	private pendingReloadSnapshot: ResourceLoaderSnapshot | undefined;

	constructor(options: DefaultResourceLoaderOptions) {
		this.cwd = resolvePath(options.cwd);
		this.agentDir = resolvePath(options.agentDir);
		this.settingsManager = options.settingsManager ?? SettingsManager.create(this.cwd, this.agentDir);
		this.eventBus = options.eventBus ?? createEventBus();
		this.packageManager = new DefaultPackageManager({
			cwd: this.cwd,
			agentDir: this.agentDir,
			settingsManager: this.settingsManager,
		});
		this.additionalExtensionPaths = options.additionalExtensionPaths ?? [];
		this.additionalSkillPaths = options.additionalSkillPaths ?? [];
		this.additionalPromptTemplatePaths = options.additionalPromptTemplatePaths ?? [];
		this.additionalThemePaths = options.additionalThemePaths ?? [];
		this.noExtensions = options.noExtensions ?? false;
		this.extensionFactories = options.extensionFactories ?? [];
		this.noSkills = options.noSkills ?? false;
		this.noPromptTemplates = options.noPromptTemplates ?? false;
		this.noThemes = options.noThemes ?? false;
		this.noContextFiles = options.noContextFiles ?? false;
		this.systemPromptSource = options.systemPrompt;
		this.appendSystemPromptSource = options.appendSystemPrompt;
		this.extensionsOverride = options.extensionsOverride;
		this.skillsOverride = options.skillsOverride;
		this.promptsOverride = options.promptsOverride;
		this.themesOverride = options.themesOverride;
		this.agentsFilesOverride = options.agentsFilesOverride;
		this.systemPromptOverride = options.systemPromptOverride;
		this.appendSystemPromptOverride = options.appendSystemPromptOverride;

		this.extensionsResult = { extensions: [], errors: [], runtime: createExtensionRuntime() };
		this.skills = [];
		this.skillDiagnostics = [];
		this.prompts = [];
		this.promptDiagnostics = [];
		this.themes = [];
		this.themeDiagnostics = [];
		this.agentsFiles = [];
		this.appendSystemPrompt = [];
		this.lastSkillPaths = [];
		this.extensionSkillSourceInfos = new Map();
		this.extensionPromptSourceInfos = new Map();
		this.extensionThemeSourceInfos = new Map();
		this.lastPromptPaths = [];
		this.lastThemePaths = [];
	}

	getExtensions(): LoadExtensionsResult {
		return this.extensionsResult;
	}

	getSkills(): { skills: Skill[]; diagnostics: ResourceDiagnostic[] } {
		return { skills: this.skills, diagnostics: this.skillDiagnostics };
	}

	/**
	 * Skills permitted by the CURRENTLY active resource profile (the loaded set intersected with the
	 * live profile filter). Use this everywhere a skill can be SELECTED, INVOKED, or shown to the
	 * agent — so neither the user (autocomplete / typed `/skill:`) nor the agent (system prompt /
	 * expansion / command API / RPC) can use a skill the active profile blocks, including after a
	 * runtime profile switch (router-managed / `/profile`). The full loaded set (`getSkills`) is
	 * reserved for the profile editor and resource listings, which must show blockable skills.
	 */
	getActiveSkills(): Skill[] {
		const filter = this.settingsManager.getResourceProfileFilter("skills");
		if (filter.allow.length === 0 && filter.block.length === 0) return this.skills;
		return this.skills.filter((s) => {
			const allowed = filter.allow.length === 0 || matchesResourceProfilePattern(s.filePath, filter.allow, this.cwd);
			const blocked = matchesResourceProfilePattern(s.filePath, filter.block, this.cwd);
			return allowed && !blocked;
		});
	}

	/**
	 * Re-scan the user skills directory plus already-discovered skill paths so a newly written or
	 * archived skill is visible to `skill` search/load without a full extension reload.
	 */
	refreshSkills(): void {
		if (this.noSkills) return;
		const userSkillsDir = resourceDir("skills", this.agentDir);
		const nextPaths = existsSync(userSkillsDir)
			? this.mergePaths(this.lastSkillPaths, [userSkillsDir])
			: this.lastSkillPaths;
		this.lastSkillPaths = nextPaths;
		this.updateSkillsFromPaths(nextPaths);
	}

	getPrompts(): { prompts: PromptTemplate[]; diagnostics: ResourceDiagnostic[] } {
		return { prompts: this.prompts, diagnostics: this.promptDiagnostics };
	}

	/**
	 * Prompt templates permitted by the CURRENTLY active resource profile (the loaded set intersected with the
	 * live profile filter). Use this everywhere a prompt template can be SELECTED, INVOKED, or shown to the
	 * agent — so neither the user (autocomplete / typed slash command) nor the agent can use a prompt template
	 * the active profile blocks. The full loaded set (`getPrompts`) is reserved for the profile editor.
	 */
	getActivePrompts(): PromptTemplate[] {
		const filter = this.settingsManager.getResourceProfileFilter("prompts");
		if (filter.allow.length === 0 && filter.block.length === 0) return this.prompts;
		return this.prompts.filter((p) => {
			const allowed = filter.allow.length === 0 || matchesResourceProfilePattern(p.filePath, filter.allow, this.cwd);
			const blocked = matchesResourceProfilePattern(p.filePath, filter.block, this.cwd);
			return allowed && !blocked;
		});
	}

	getThemes(): { themes: Theme[]; diagnostics: ResourceDiagnostic[] } {
		return { themes: this.themes, diagnostics: this.themeDiagnostics };
	}

	/**
	 * Themes permitted by the CURRENTLY active resource profile.
	 */
	getActiveThemes(): Theme[] {
		const filter = this.settingsManager.getResourceProfileFilter("themes");
		if (filter.allow.length === 0 && filter.block.length === 0) return this.themes;
		return this.themes.filter((t) => {
			if (!t.sourcePath) return true;
			const allowed =
				filter.allow.length === 0 || matchesResourceProfilePattern(t.sourcePath, filter.allow, this.cwd);
			const blocked = matchesResourceProfilePattern(t.sourcePath, filter.block, this.cwd);
			return allowed && !blocked;
		});
	}

	getAgentsFiles(): { agentsFiles: Array<{ path: string; content?: string }> } {
		return { agentsFiles: this.agentsFiles };
	}

	/** Warnings about context files withheld by the active profile (empty when none). */
	getAgentsDiagnostics(): ResourceDiagnostic[] {
		return this.agentsDiagnostics;
	}

	/**
	 * Profile-INDEPENDENT discovery for the profile editor's universe (same rule as
	 * getDiscoverableExtensionPaths): the full pre-filter path sets retained from the last
	 * reload. Discovery is metadata, not loading — granting a currently-blocked skill/prompt/
	 * context file requires being able to SEE it; strict UAC only forbids reading denied
	 * CONTENT into the session.
	 */
	getDiscoverableSkillPaths(): string[] {
		return [...this.discoverableSkillPaths];
	}

	getDiscoverablePromptPaths(): string[] {
		return [...this.discoverablePromptPaths];
	}

	getDiscoverableAgentsFilePaths(): string[] {
		return [...this.lastAgentsFilePaths];
	}

	getSystemPrompt(): string | undefined {
		return this.systemPrompt;
	}

	getAppendSystemPrompt(): string[] {
		return this.appendSystemPrompt;
	}

	/**
	 * Get all discoverable extension paths (enabled and disabled).
	 * Used for profile resource filtering to show the full universe of available extensions.
	 */
	async getDiscoverableExtensionPaths(): Promise<string[]> {
		await this.settingsManager.reload();
		const resolvedPaths = await this.packageManager.resolve();
		const cliExtensionPaths = await this.packageManager.resolveExtensionSources(this.additionalExtensionPaths, {
			temporary: true,
		});
		// Return all paths (enabled and disabled) from resolved, CLI, AND external-resource-root sources.
		// External-root extensions are loaded just like the others (see reload), so they MUST appear in the
		// profile editor's universe too — otherwise they're active but unblockable, and the editor wrongly
		// shows "(none available)" while they run (bug: external extensions invisible to the profile editor).
		const allPaths = new Set<string>();
		for (const resource of resolvedPaths.extensions) {
			allPaths.add(resource.path);
		}
		for (const resource of cliExtensionPaths.extensions) {
			allPaths.add(resource.path);
		}
		for (const p of this.discoverExternalExtensionPaths()) {
			allPaths.add(p);
		}
		for (const p of this.discoverBundledExtensionPaths()) {
			allPaths.add(p);
		}
		return Array.from(allPaths);
	}

	/**
	 * Discover extension directories under every external resource root (`<root>/extensions/<name>` with an
	 * index.ts/index.js/package.json). Single source of truth shared by the load path and the profile
	 * editor's universe so the two never diverge.
	 */
	private discoverExternalExtensionPaths(): string[] {
		return this.settingsManager
			.getEffectiveExternalResourceRoots()
			.flatMap((root) => this.discoverExtensionPathsInDirectory(join(root, "extensions")));
	}

	private discoverBundledExtensionPaths(): string[] {
		return this.discoverExtensionPathsInDirectory(getBundledExtensionsDir());
	}

	private discoverExtensionPathsInDirectory(extensionsDir: string): string[] {
		return readResourceDirectory(extensionsDir, { followSymbolicLinks: true })
			.filter((entry) => entry.isDirectory)
			.map((entry) => entry.path)
			.filter(
				(entryPath) =>
					existsSync(join(entryPath, "index.ts")) ||
					existsSync(join(entryPath, "index.js")) ||
					existsSync(join(entryPath, "package.json")),
			);
	}

	/**
	 * Get a loaded extension by path.
	 * Matches by path or resolvedPath.
	 */
	getLoadedExtension(extensionPath: string): Extension | undefined {
		return this.extensionsResult.extensions.find(
			(ext) => ext.path === extensionPath || ext.resolvedPath === extensionPath,
		);
	}

	/**
	 * Remove and return a loaded extension from the extensions array.
	 */
	removeLoadedExtension(extensionPath: string): Extension | undefined {
		const index = this.extensionsResult.extensions.findIndex(
			(ext) => ext.path === extensionPath || ext.resolvedPath === extensionPath,
		);
		if (index === -1) return undefined;
		const [ext] = this.extensionsResult.extensions.splice(index, 1);
		return ext;
	}

	/**
	 * Load a single extension with fresh import, reusing the shared runtime.
	 * Returns the loaded extension or null with error details.
	 */
	async loadSingleExtension(extensionPath: string): Promise<{ extension: Extension | null; error: string | null }> {
		const result = await loadExtension(extensionPath, this.cwd, this.eventBus, this.extensionsResult.runtime, {
			fresh: true,
			agentDir: this.agentDir,
		});
		if (result.extension && !result.error) {
			const loaded = result.extension;
			// Drop any stale generation at the same path, then register the freshly loaded one so
			// _buildRuntime() aggregates it.
			this.extensionsResult.extensions = this.extensionsResult.extensions.filter(
				(e) => e.path !== loaded.path && e.resolvedPath !== loaded.resolvedPath,
			);
			this.extensionsResult.extensions.push(loaded);
		}
		return result;
	}

	async loadIsolatedExtension(
		extensionPath: string,
		cwd: string,
	): Promise<{ extension: Extension | null; error: string | null }> {
		return loadExtension(extensionPath, cwd, createEventBus(), createExtensionRuntime(), {
			fresh: true,
			agentDir: this.agentDir,
		});
	}

	/**
	 * Apply the active resource-profile allow/block to a list of discovered resource paths.
	 * Used for every source that does NOT pass through the package manager's
	 * resolve/applyResourceProfileFilters pipeline (external roots, bundled defaults, and
	 * extension-contributed resources) so no source bypasses the active profile.
	 */
	private filterPathsByProfile(paths: string[], kind: ResourceProfileKind): string[] {
		const filter = this.settingsManager.getResourceProfileFilter(kind);
		if (filter.allow.length === 0 && filter.block.length === 0) return paths;
		return paths.filter((p) => {
			const allowed = filter.allow.length === 0 || matchesResourceProfilePattern(p, filter.allow, this.cwd);
			const blocked = matchesResourceProfilePattern(p, filter.block, this.cwd);
			return allowed && !blocked;
		});
	}

	extendResources(paths: ResourceExtensionPaths): void {
		// Extension-contributed resources (via the resources_discover event) must respect the
		// active resource profile too — otherwise an allowed extension can re-introduce skills/
		// prompts/themes the profile blocks.
		const allowPath = (entry: { path: string }, kind: ResourceProfileKind): boolean =>
			this.filterPathsByProfile([entry.path], kind).length > 0;
		const skillPaths = this.normalizeExtensionPaths(paths.skillPaths ?? []).filter((e) => allowPath(e, "skills"));
		const promptPaths = this.normalizeExtensionPaths(paths.promptPaths ?? []).filter((e) => allowPath(e, "prompts"));
		const themePaths = this.normalizeExtensionPaths(paths.themePaths ?? []).filter((e) => allowPath(e, "themes"));

		for (const entry of skillPaths) {
			this.extensionSkillSourceInfos.set(entry.path, createSourceInfo(entry.path, entry.metadata));
		}
		for (const entry of promptPaths) {
			this.extensionPromptSourceInfos.set(entry.path, createSourceInfo(entry.path, entry.metadata));
		}
		for (const entry of themePaths) {
			this.extensionThemeSourceInfos.set(entry.path, createSourceInfo(entry.path, entry.metadata));
		}

		if (skillPaths.length > 0) {
			this.lastSkillPaths = this.mergePaths(
				this.lastSkillPaths,
				skillPaths.map((entry) => entry.path),
			);
			this.updateSkillsFromPaths(this.lastSkillPaths);
		}

		if (promptPaths.length > 0) {
			this.lastPromptPaths = this.mergePaths(
				this.lastPromptPaths,
				promptPaths.map((entry) => entry.path),
			);
			this.updatePromptsFromPaths(this.lastPromptPaths);
		}

		if (themePaths.length > 0) {
			this.lastThemePaths = this.mergePaths(
				this.lastThemePaths,
				themePaths.map((entry) => entry.path),
			);
			this.updateThemesFromPaths(this.lastThemePaths);
		}
	}

	private createSnapshot(): ResourceLoaderSnapshot {
		return {
			extensionsResult: this.extensionsResult,
			skills: this.skills,
			skillDiagnostics: this.skillDiagnostics,
			prompts: this.prompts,
			promptDiagnostics: this.promptDiagnostics,
			themes: this.themes,
			themeDiagnostics: this.themeDiagnostics,
			agentsFiles: this.agentsFiles,
			agentsDiagnostics: this.agentsDiagnostics,
			lastAgentsFilePaths: this.lastAgentsFilePaths,
			discoverableSkillPaths: this.discoverableSkillPaths,
			discoverablePromptPaths: this.discoverablePromptPaths,
			systemPrompt: this.systemPrompt,
			appendSystemPrompt: this.appendSystemPrompt,
			lastSkillPaths: this.lastSkillPaths,
			extensionSkillSourceInfos: this.extensionSkillSourceInfos,
			extensionPromptSourceInfos: this.extensionPromptSourceInfos,
			extensionThemeSourceInfos: this.extensionThemeSourceInfos,
			lastPromptPaths: this.lastPromptPaths,
			lastThemePaths: this.lastThemePaths,
		};
	}

	private restoreSnapshot(snapshot: ResourceLoaderSnapshot): void {
		this.extensionsResult = snapshot.extensionsResult;
		this.skills = snapshot.skills;
		this.skillDiagnostics = snapshot.skillDiagnostics;
		this.prompts = snapshot.prompts;
		this.promptDiagnostics = snapshot.promptDiagnostics;
		this.themes = snapshot.themes;
		this.themeDiagnostics = snapshot.themeDiagnostics;
		this.agentsFiles = snapshot.agentsFiles;
		this.agentsDiagnostics = snapshot.agentsDiagnostics;
		this.lastAgentsFilePaths = snapshot.lastAgentsFilePaths;
		this.discoverableSkillPaths = snapshot.discoverableSkillPaths;
		this.discoverablePromptPaths = snapshot.discoverablePromptPaths;
		this.systemPrompt = snapshot.systemPrompt;
		this.appendSystemPrompt = snapshot.appendSystemPrompt;
		this.lastSkillPaths = snapshot.lastSkillPaths;
		this.extensionSkillSourceInfos = snapshot.extensionSkillSourceInfos;
		this.extensionPromptSourceInfos = snapshot.extensionPromptSourceInfos;
		this.extensionThemeSourceInfos = snapshot.extensionThemeSourceInfos;
		this.lastPromptPaths = snapshot.lastPromptPaths;
		this.lastThemePaths = snapshot.lastThemePaths;
	}

	async commitReload(): Promise<void> {
		if (!this.pendingReloadSnapshot) return;
		await disposeExtensionEventSubscriptions(this.pendingReloadSnapshot.extensionsResult.extensions);
		this.pendingReloadSnapshot = undefined;
	}

	async rollbackReload(): Promise<void> {
		if (!this.pendingReloadSnapshot) return;
		await disposeExtensionEventSubscriptions(this.extensionsResult.extensions);
		this.restoreSnapshot(this.pendingReloadSnapshot);
		this.pendingReloadSnapshot = undefined;
	}

	async dispose(): Promise<void> {
		if (this.pendingReloadSnapshot) {
			await disposeExtensionEventSubscriptions(this.pendingReloadSnapshot.extensionsResult.extensions);
			this.pendingReloadSnapshot = undefined;
		}
		await disposeExtensionEventSubscriptions(this.extensionsResult.extensions);
		this.extensionsResult = { extensions: [], errors: [], runtime: createExtensionRuntime() };
	}

	async reload(options: ResourceReloadOptions = {}): Promise<void> {
		const snapshot = this.createSnapshot();
		this.pendingReloadSnapshot = undefined;
		try {
			if (!options.skipSettingsReload) {
				await this.settingsManager.reload();
			}
			const resolvedPaths = await this.packageManager.resolve();
			const cliExtensionPaths = await this.packageManager.resolveExtensionSources(this.additionalExtensionPaths, {
				temporary: true,
			});
			const metadataByPath = new Map<string, PathMetadata>();

			this.extensionSkillSourceInfos = new Map();
			this.extensionPromptSourceInfos = new Map();
			this.extensionThemeSourceInfos = new Map();

			// Helper to extract enabled paths and store metadata
			const getEnabledResources = (
				resources: Array<{ path: string; enabled: boolean; metadata: PathMetadata }>,
			): Array<{ path: string; enabled: boolean; metadata: PathMetadata }> => {
				for (const r of resources) {
					if (!metadataByPath.has(r.path)) {
						metadataByPath.set(r.path, r.metadata);
					}
				}
				return resources.filter((r) => r.enabled);
			};

			const getEnabledPaths = (
				resources: Array<{ path: string; enabled: boolean; metadata: PathMetadata }>,
			): string[] => getEnabledResources(resources).map((r) => r.path);

			const enabledExtensions = getEnabledPaths(resolvedPaths.extensions);
			const enabledSkillResources = getEnabledResources(resolvedPaths.skills);
			const enabledPrompts = getEnabledPaths(resolvedPaths.prompts);
			const enabledThemes = getEnabledPaths(resolvedPaths.themes);

			const mapSkillPath = (resource: { path: string; metadata: PathMetadata }): string => {
				if (resource.metadata.source !== "auto" && resource.metadata.origin !== "package") {
					return resource.path;
				}
				try {
					const stats = statSync(resource.path);
					if (!stats.isDirectory()) {
						return resource.path;
					}
				} catch {
					return resource.path;
				}
				const skillFile = join(resource.path, "SKILL.md");
				if (existsSync(skillFile)) {
					if (!metadataByPath.has(skillFile)) {
						metadataByPath.set(skillFile, resource.metadata);
					}
					return skillFile;
				}
				return resource.path;
			};

			const enabledSkills = enabledSkillResources.map(mapSkillPath);

			// Add CLI paths metadata
			for (const r of cliExtensionPaths.extensions) {
				if (!metadataByPath.has(r.path)) {
					metadataByPath.set(r.path, { source: "cli", scope: "temporary", origin: "top-level" });
				}
			}
			for (const r of cliExtensionPaths.skills) {
				if (!metadataByPath.has(r.path)) {
					metadataByPath.set(r.path, { source: "cli", scope: "temporary", origin: "top-level" });
				}
			}

			const cliEnabledExtensions = getEnabledPaths(cliExtensionPaths.extensions);
			const cliEnabledSkills = getEnabledPaths(cliExtensionPaths.skills);
			const cliEnabledPrompts = getEnabledPaths(cliExtensionPaths.prompts);
			const cliEnabledThemes = getEnabledPaths(cliExtensionPaths.themes);

			// Gather effective external resource roots
			const effectiveRoots = this.settingsManager.getEffectiveExternalResourceRoots();

			const filterPathsByProfile = (paths: string[], kind: ResourceProfileKind): string[] =>
				this.filterPathsByProfile(paths, kind);

			// Authority-bearing bundled extensions stay profile-gated. Passive default-on extensions
			// are the narrow exception and still honor explicit user/profile blocks.
			const bundledExtensions = this.discoverBundledExtensionPaths();
			for (const entryPath of bundledExtensions) {
				metadataByPath.set(entryPath, {
					source: "bundled",
					scope: "user",
					origin: "top-level",
					baseDir: getBundledExtensionsDir(),
				});
			}

			// Discover external extensions (same source the profile editor uses, so they stay in sync).
			const externalExtensions = this.discoverExternalExtensionPaths();
			for (const entryPath of externalExtensions) {
				metadataByPath.set(entryPath, { source: "external", scope: "user", origin: "top-level" });
			}
			const hasProfileExtensionAuthority = hasProfileExtensionImportAuthority(this.settingsManager);
			const extensionProfileFilter = this.settingsManager.getResourceProfileFilter("extensions");
			// CLI paths carry an exact owner grant, but an active profile and user disables still bound
			// that authority. Other disk extensions require an active profile before their modules cross
			// the import boundary; discovery remains metadata-only when no profile is active.
			const profileFilteredCliExtensions = filterPathsByProfile(cliEnabledExtensions, "extensions");
			const profileFilteredConfiguredExtensions = hasProfileExtensionAuthority ? enabledExtensions : [];
			const defaultOnBundledExtensions = bundledExtensions.filter(
				(entryPath) =>
					isDefaultOnBundledExtension(entryPath, "bundled") &&
					isExtensionPathAllowedForImport(
						this.settingsManager,
						entryPath,
						"default-on",
						getBundledExtensionsDir(),
					),
			);
			const profileFilteredBundledExtensions =
				!hasProfileExtensionAuthority || extensionProfileFilter.allow.length === 0
					? []
					: filterPathsByProfile(bundledExtensions, "extensions");
			const profileFilteredExternalExtensions = hasProfileExtensionAuthority
				? filterPathsByProfile(externalExtensions, "extensions")
				: [];

			const extensionPaths = this.noExtensions
				? profileFilteredCliExtensions
				: this.mergePaths(profileFilteredCliExtensions, [
						...defaultOnBundledExtensions,
						...profileFilteredBundledExtensions,
						...profileFilteredConfiguredExtensions,
						...profileFilteredExternalExtensions,
					]);

			const extensionsResult = await loadExtensions(extensionPaths, this.cwd, this.eventBus, {
				agentDir: this.agentDir,
			});
			const inlineExtensions = await this.loadExtensionFactories(extensionsResult.runtime);
			extensionsResult.extensions.push(...inlineExtensions.extensions);
			extensionsResult.errors.push(...inlineExtensions.errors);

			// Detect extension conflicts (tools, commands, flags with same names from different extensions)
			// Keep all extensions loaded. Conflicts are reported as diagnostics, and precedence is handled by load order.
			const conflicts = this.detectExtensionConflicts(extensionsResult.extensions);
			for (const conflict of conflicts) {
				extensionsResult.errors.push({ path: conflict.path, error: conflict.message });
			}

			for (const p of this.additionalExtensionPaths) {
				if (isLocalPath(p)) {
					const resolved = this.resolveResourcePath(p);
					if (!existsSync(resolved)) {
						extensionsResult.errors.push({ path: resolved, error: `Extension path does not exist: ${resolved}` });
					}
				}
			}
			const resolvedExtensionsResult = this.extensionsOverride
				? this.extensionsOverride(extensionsResult)
				: extensionsResult;
			if (options.failOnExtensionErrors && resolvedExtensionsResult.errors.length > 0) {
				const summary = resolvedExtensionsResult.errors
					.slice(0, 6)
					.map((error) => `${error.path}: ${error.error}`)
					.join("; ");
				throw new Error(`Extension reload failed preflight: ${summary}`);
			}
			this.extensionsResult = resolvedExtensionsResult;
			this.applyExtensionSourceInfo(this.extensionsResult.extensions, metadataByPath);

			// Discover external skills
			const externalSkills: string[] = [];
			for (const root of effectiveRoots) {
				const skillsDir = join(root, "skills");
				for (const entry of readResourceDirectory(skillsDir, { followSymbolicLinks: true })) {
					if (!entry.isDirectory) continue;
					const skillFile = join(entry.path, "SKILL.md");
					const targetPath = existsSync(skillFile) ? skillFile : entry.path;
					externalSkills.push(targetPath);
					metadataByPath.set(targetPath, { source: "external", scope: "user", origin: "top-level" });
				}
			}

			// Build skill paths with precedence: CLI > user/project > bundled > additional.
			// Bundled skills are expanded into individual SKILL.md paths so they pass through the
			// resource-profile filter exactly like user/external skills (no source bypasses the profile).
			const bundledSkillsDir = getBundledSkillsDir();
			const bundledSkillPaths = readResourceDirectory(bundledSkillsDir, { followSymbolicLinks: false })
				.filter((entry) => entry.isDirectory)
				.map((entry) => {
					const skillFile = join(entry.path, "SKILL.md");
					return existsSync(skillFile) ? skillFile : entry.path;
				});
			const skillPaths = this.noSkills
				? this.mergePaths([...cliEnabledSkills], this.additionalSkillPaths)
				: this.mergePaths(
						[...cliEnabledSkills, ...enabledSkills, ...externalSkills, ...bundledSkillPaths],
						this.additionalSkillPaths,
					);

			this.lastSkillPaths = skillPaths;
			// Discovery universe: ALL package-resolved skills (profile-denied entries survive
			// resolve() with enabled=false) plus every other source, pre-filter.
			this.discoverableSkillPaths = this.mergePaths(
				[
					...cliEnabledSkills,
					...enabledSkillResources.map(mapSkillPath),
					...resolvedPaths.skills.map(mapSkillPath),
					...externalSkills,
					...bundledSkillPaths,
				],
				this.additionalSkillPaths,
			);
			this.updateSkillsFromPaths(skillPaths, metadataByPath);
			for (const p of this.additionalSkillPaths) {
				if (isLocalPath(p)) {
					const resolved = this.resolveResourcePath(p);
					if (!existsSync(resolved) && !this.skillDiagnostics.some((d) => d.path === resolved)) {
						this.skillDiagnostics.push({ type: "error", message: "Skill path does not exist", path: resolved });
					}
				}
			}

			// Discover external prompts
			const externalPrompts: string[] = [];
			for (const root of effectiveRoots) {
				const promptsDir = join(root, "prompts");
				if (existsSync(promptsDir)) {
					const files = collectResourceFilesRecursively(promptsDir, (entry) => entry.name.endsWith(".md"), {
						followSymbolicLinks: true,
						skipHidden: true,
						skipNodeModules: true,
					});
					for (const f of files) {
						externalPrompts.push(f);
						metadataByPath.set(f, { source: "external", scope: "user", origin: "top-level" });
					}
				}
			}

			// Bundled prompts expanded into individual files so they pass through the profile filter too.
			const bundledPromptsDir = getBundledPromptsDir();
			const bundledPromptPaths = existsSync(bundledPromptsDir)
				? collectResourceFilesRecursively(bundledPromptsDir, (entry) => entry.name.endsWith(".md"), {
						followSymbolicLinks: true,
						skipHidden: true,
						skipNodeModules: true,
					})
				: [];
			const promptPaths = this.noPromptTemplates
				? this.mergePaths(cliEnabledPrompts, this.additionalPromptTemplatePaths)
				: this.mergePaths(
						[...cliEnabledPrompts, ...enabledPrompts, ...externalPrompts, ...bundledPromptPaths],
						this.additionalPromptTemplatePaths,
					);

			this.lastPromptPaths = promptPaths;
			this.discoverablePromptPaths = this.mergePaths(
				[
					...cliEnabledPrompts,
					...enabledPrompts,
					...resolvedPaths.prompts.map((resource) => resource.path),
					...externalPrompts,
					...bundledPromptPaths,
				],
				this.additionalPromptTemplatePaths,
			);
			this.updatePromptsFromPaths(promptPaths, metadataByPath);
			for (const p of this.additionalPromptTemplatePaths) {
				if (isLocalPath(p)) {
					const resolved = this.resolveResourcePath(p);
					if (!existsSync(resolved) && !this.promptDiagnostics.some((d) => d.path === resolved)) {
						this.promptDiagnostics.push({
							type: "error",
							message: "Prompt template path does not exist",
							path: resolved,
						});
					}
				}
			}

			// Discover external themes
			const externalThemes: string[] = [];
			for (const root of effectiveRoots) {
				const themesDir = join(root, "themes");
				if (existsSync(themesDir)) {
					const files = collectResourceFilesRecursively(themesDir, (entry) => entry.name.endsWith(".json"), {
						followSymbolicLinks: true,
						skipHidden: true,
						skipNodeModules: true,
					});
					for (const f of files) {
						externalThemes.push(f);
						metadataByPath.set(f, { source: "external", scope: "user", origin: "top-level" });
					}
				}
			}

			const themePaths = this.noThemes
				? this.mergePaths(cliEnabledThemes, this.additionalThemePaths)
				: this.mergePaths([...cliEnabledThemes, ...enabledThemes, ...externalThemes], this.additionalThemePaths);

			this.lastThemePaths = themePaths;
			this.updateThemesFromPaths(themePaths, metadataByPath);
			for (const p of this.additionalThemePaths) {
				const resolved = this.resolveResourcePath(p);
				if (!existsSync(resolved) && !this.themeDiagnostics.some((d) => d.path === resolved)) {
					this.themeDiagnostics.push({ type: "error", message: "Theme path does not exist", path: resolved });
				}
			}

			// Global agent-dir files always load. Project/ancestor files are discovered only when
			// this directory/project opts in (`projectContextFiles: "on-demand"`) and `-nc` is not set.
			const rawAgentsFiles = loadRawProjectContextFiles({
				cwd: this.cwd,
				agentDir: this.agentDir,
				projectTrusted: this.settingsManager.isProjectTrusted(),
				includeProject: !this.noContextFiles && this.settingsManager.getProjectContextFiles() === "on-demand",
			});
			const agentEmbeddedProfiles: Record<string, ResourceProfileSettings> = {};
			const activeProfileNames = this.settingsManager.getActiveResourceProfileNames();
			// DISCOVERY read (metadata only, never loading): an embedded <resource-profile> block can live
			// in ANY context file — including one the resulting agents filter then denies (AGENTS.md
			// defining a profile that blocks GEMINI.md is the canonical bootstrap circularity). The filter
			// cannot be known without first scanning every candidate file's raw content, so path-based
			// pre-filtering is impossible for the agents kind. Only profile-block config is extracted here;
			// a denied file's instructional content is never sanitized, threat-scanned, or exposed — it is
			// dropped by the filter below — so the never-read-denied-CONTENT invariant holds.
			for (const file of rawAgentsFiles) {
				try {
					const { profiles } = parseResourceProfileBlocks(file.rawContent, { profileNames: activeProfileNames });
					Object.assign(agentEmbeddedProfiles, mergeResourceProfileMap(agentEmbeddedProfiles, profiles));
				} catch {}
			}
			this.settingsManager.addDiscoveredResourceProfileDefinitions(agentEmbeddedProfiles);
			const agentProfileFilter = this.settingsManager.getResourceProfileFilter("agents");
			// Editor universe: the FULL pre-filter path set (discovery is metadata, not loading).
			this.lastAgentsFilePaths = rawAgentsFiles.map((file) => file.path);
			const agentsFiles = {
				agentsFiles: rawAgentsFiles
					.filter((file) => {
						const allowed =
							agentProfileFilter.allow.length === 0 ||
							matchesResourceProfilePattern(file.path, agentProfileFilter.allow, this.cwd);
						const blocked = matchesResourceProfilePattern(file.path, agentProfileFilter.block, this.cwd);
						return allowed && !blocked;
					})
					// Global files are sanitized and injected. Project files stay path-only so the
					// model can read them on demand; denied files are dropped before this map.
					.map((file) =>
						isGlobalAgentsFile(file.path, this.agentDir)
							? { path: file.path, content: sanitizeContextFileContent(file.path, file.rawContent) }
							: { path: file.path },
					),
			};
			// Strict UAC silently denying AGENTS.md/CLAUDE.md context is a sharp footgun for lean
			// profiles — surface it loudly instead of letting instructions vanish without a trace.
			const withheldAgentsCount = rawAgentsFiles.length - agentsFiles.agentsFiles.length;
			this.agentsDiagnostics =
				withheldAgentsCount > 0
					? [
							{
								type: "warning",
								message: `${withheldAgentsCount} context file(s) (AGENTS.md/CLAUDE.md) withheld by the active resource profile — grant the "agents" kind to restore them`,
							},
						]
					: [];
			const resolvedAgentsFiles = this.agentsFilesOverride ? this.agentsFilesOverride(agentsFiles) : agentsFiles;
			this.agentsFiles = resolvedAgentsFiles.agentsFiles;

			const baseSystemPrompt = resolvePromptInput(
				this.systemPromptSource ?? this.discoverSystemPromptFile(),
				"system prompt",
			);
			this.systemPrompt = this.systemPromptOverride ? this.systemPromptOverride(baseSystemPrompt) : baseSystemPrompt;

			const appendSources =
				this.appendSystemPromptSource ??
				(this.discoverAppendSystemPromptFile() ? [this.discoverAppendSystemPromptFile()!] : []);
			const baseAppend = appendSources
				.map((s) => resolvePromptInput(s, "append system prompt"))
				.filter((s): s is string => s !== undefined);
			this.appendSystemPrompt = this.appendSystemPromptOverride
				? this.appendSystemPromptOverride(baseAppend)
				: baseAppend;
			if (options.deferExtensionDispose) {
				this.pendingReloadSnapshot = snapshot;
			} else {
				await disposeExtensionEventSubscriptions(snapshot.extensionsResult.extensions);
			}
		} catch (error) {
			if (this.extensionsResult !== snapshot.extensionsResult) {
				await disposeExtensionEventSubscriptions(this.extensionsResult.extensions);
			}
			this.restoreSnapshot(snapshot);
			throw error;
		}
	}

	private normalizeExtensionPaths(
		entries: Array<{ path: string; metadata: PathMetadata }>,
	): Array<{ path: string; metadata: PathMetadata }> {
		return entries.map((entry) => {
			const metadata = entry.metadata.baseDir
				? { ...entry.metadata, baseDir: this.resolveResourcePath(entry.metadata.baseDir) }
				: entry.metadata;
			return {
				path: this.resolveResourcePath(entry.path),
				metadata,
			};
		});
	}

	private updateSkillsFromPaths(skillPaths: string[], metadataByPath?: Map<string, PathMetadata>): void {
		let skillsResult: { skills: Skill[]; diagnostics: ResourceDiagnostic[] };
		if (this.noSkills && skillPaths.length === 0) {
			skillsResult = { skills: [], diagnostics: [] };
		} else {
			skillsResult = loadSkills({
				cwd: this.cwd,
				agentDir: this.agentDir,
				skillPaths,
				includeDefaults: false,
				// Profile UAC: denied skill files are never read from disk.
				isPathAllowed: (path) => this.filterPathsByProfile([path], "skills").length > 0,
			});
		}
		const resolvedSkills = this.skillsOverride ? this.skillsOverride(skillsResult) : skillsResult;
		this.skills = resolvedSkills.skills.map((skill) => ({
			...skill,
			sourceInfo:
				this.findSourceInfoForPath(skill.filePath, this.extensionSkillSourceInfos, metadataByPath) ??
				skill.sourceInfo ??
				this.getDefaultSourceInfoForPath(skill.filePath),
		}));
		this.skillDiagnostics = resolvedSkills.diagnostics;
	}

	private updatePromptsFromPaths(promptPaths: string[], metadataByPath?: Map<string, PathMetadata>): void {
		let promptsResult: { prompts: PromptTemplate[]; diagnostics: ResourceDiagnostic[] };
		if (this.noPromptTemplates && promptPaths.length === 0) {
			promptsResult = { prompts: [], diagnostics: [] };
		} else {
			const allPrompts = loadPromptTemplates({
				cwd: this.cwd,
				agentDir: this.agentDir,
				promptPaths,
				includeDefaults: false,
				// Profile UAC: denied prompt templates are never read from disk.
				isPathAllowed: (path) => this.filterPathsByProfile([path], "prompts").length > 0,
			});
			promptsResult = this.dedupePrompts(allPrompts);
		}
		const resolvedPrompts = this.promptsOverride ? this.promptsOverride(promptsResult) : promptsResult;
		this.prompts = resolvedPrompts.prompts.map((prompt) => ({
			...prompt,
			sourceInfo:
				this.findSourceInfoForPath(prompt.filePath, this.extensionPromptSourceInfos, metadataByPath) ??
				prompt.sourceInfo ??
				this.getDefaultSourceInfoForPath(prompt.filePath),
		}));
		this.promptDiagnostics = resolvedPrompts.diagnostics;
	}

	private updateThemesFromPaths(themePaths: string[], metadataByPath?: Map<string, PathMetadata>): void {
		let themesResult: { themes: Theme[]; diagnostics: ResourceDiagnostic[] };
		if (this.noThemes && themePaths.length === 0) {
			themesResult = { themes: [], diagnostics: [] };
		} else {
			const loaded = this.loadThemes(themePaths, false);
			const deduped = this.dedupeThemes(loaded.themes);
			themesResult = { themes: deduped.themes, diagnostics: [...loaded.diagnostics, ...deduped.diagnostics] };
		}
		const resolvedThemes = this.themesOverride ? this.themesOverride(themesResult) : themesResult;
		this.themes = resolvedThemes.themes.map((theme) => {
			const sourcePath = theme.sourcePath;
			theme.sourceInfo = sourcePath
				? (this.findSourceInfoForPath(sourcePath, this.extensionThemeSourceInfos, metadataByPath) ??
					theme.sourceInfo ??
					this.getDefaultSourceInfoForPath(sourcePath))
				: theme.sourceInfo;
			return theme;
		});
		this.themeDiagnostics = resolvedThemes.diagnostics;
	}

	private applyExtensionSourceInfo(extensions: Extension[], metadataByPath: Map<string, PathMetadata>): void {
		for (const extension of extensions) {
			extension.sourceInfo =
				this.findSourceInfoForPath(extension.path, undefined, metadataByPath) ??
				this.getDefaultSourceInfoForPath(extension.path);
			for (const command of extension.commands.values()) {
				command.sourceInfo = extension.sourceInfo;
			}
			for (const tool of extension.tools.values()) {
				tool.sourceInfo = extension.sourceInfo;
			}
		}
	}

	private findSourceInfoForPath(
		resourcePath: string,
		extraSourceInfos?: Map<string, SourceInfo>,
		metadataByPath?: Map<string, PathMetadata>,
	): SourceInfo | undefined {
		if (!resourcePath) {
			return undefined;
		}

		if (resourcePath.startsWith("<")) {
			return this.getDefaultSourceInfoForPath(resourcePath);
		}

		const normalizedResourcePath = resolve(resourcePath);
		if (extraSourceInfos) {
			for (const [sourcePath, sourceInfo] of extraSourceInfos.entries()) {
				const normalizedSourcePath = resolve(sourcePath);
				if (
					normalizedResourcePath === normalizedSourcePath ||
					normalizedResourcePath.startsWith(`${normalizedSourcePath}${sep}`)
				) {
					return { ...sourceInfo, path: resourcePath };
				}
			}
		}

		if (metadataByPath) {
			const exact = metadataByPath.get(normalizedResourcePath) ?? metadataByPath.get(resourcePath);
			if (exact) {
				return createSourceInfo(resourcePath, exact);
			}

			for (const [sourcePath, metadata] of metadataByPath.entries()) {
				const normalizedSourcePath = resolve(sourcePath);
				if (
					normalizedResourcePath === normalizedSourcePath ||
					normalizedResourcePath.startsWith(`${normalizedSourcePath}${sep}`)
				) {
					return createSourceInfo(resourcePath, metadata);
				}
			}
		}

		return undefined;
	}

	private getDefaultSourceInfoForPath(filePath: string): SourceInfo {
		if (filePath.startsWith("<") && filePath.endsWith(">")) {
			return {
				path: filePath,
				source: filePath.slice(1, -1).split(":")[0] || "temporary",
				scope: "temporary",
				origin: "top-level",
			};
		}

		const normalizedPath = resolve(filePath);
		const agentRoots = [
			resourceDir("skills", this.agentDir),
			resourceDir("prompts", this.agentDir),
			resourceDir("themes", this.agentDir),
			resourceDir("extensions", this.agentDir),
		];
		const projectRoots = [
			join(this.cwd, CONFIG_DIR_NAME, "skills"),
			join(this.cwd, CONFIG_DIR_NAME, "prompts"),
			join(this.cwd, CONFIG_DIR_NAME, "themes"),
			join(this.cwd, CONFIG_DIR_NAME, "extensions"),
		];

		for (const root of agentRoots) {
			if (isResourcePathWithin(normalizedPath, root)) {
				return { path: filePath, source: "local", scope: "user", origin: "top-level", baseDir: root };
			}
		}

		for (const root of projectRoots) {
			if (isResourcePathWithin(normalizedPath, root)) {
				return { path: filePath, source: "local", scope: "project", origin: "top-level", baseDir: root };
			}
		}

		return {
			path: filePath,
			source: "local",
			scope: "temporary",
			origin: "top-level",
			baseDir: statSync(normalizedPath).isDirectory() ? normalizedPath : resolve(normalizedPath, ".."),
		};
	}

	private mergePaths(primary: string[], additional: string[]): string[] {
		const merged: string[] = [];
		const seen = new Set<string>();

		for (const p of [...primary, ...additional]) {
			const resolved = this.resolveResourcePath(p);
			const canonicalPath = canonicalizePath(resolved);
			if (seen.has(canonicalPath)) continue;
			seen.add(canonicalPath);
			merged.push(resolved);
		}

		return merged;
	}

	private resolveResourcePath(p: string): string {
		return resolvePath(p, this.cwd, { trim: true });
	}

	private loadThemes(
		paths: string[],
		includeDefaults: boolean = true,
	): {
		themes: Theme[];
		diagnostics: ResourceDiagnostic[];
	} {
		const themes: Theme[] = [];
		const diagnostics: ResourceDiagnostic[] = [];
		if (includeDefaults) {
			const defaultDirs = [resourceDir("themes", this.agentDir), join(this.cwd, CONFIG_DIR_NAME, "themes")];

			for (const dir of defaultDirs) {
				this.loadThemesFromDir(dir, themes, diagnostics);
			}
		}

		for (const p of paths) {
			const resolved = this.resolveResourcePath(p);
			if (!existsSync(resolved)) {
				diagnostics.push({ type: "warning", message: "theme path does not exist", path: resolved });
				continue;
			}

			try {
				const stats = statSync(resolved);
				if (stats.isDirectory()) {
					this.loadThemesFromDir(resolved, themes, diagnostics);
				} else if (stats.isFile() && resolved.endsWith(".json")) {
					this.loadThemeFromFile(resolved, themes, diagnostics);
				} else {
					diagnostics.push({ type: "warning", message: "theme path is not a json file", path: resolved });
				}
			} catch (error) {
				const message = error instanceof Error ? error.message : "failed to read theme path";
				diagnostics.push({ type: "warning", message, path: resolved });
			}
		}

		return { themes, diagnostics };
	}

	private loadThemesFromDir(dir: string, themes: Theme[], diagnostics: ResourceDiagnostic[]): void {
		if (!existsSync(dir)) {
			return;
		}

		const entries = readResourceDirectory(dir, {
			followSymbolicLinks: true,
			onDirectoryReadError: (path, error) => {
				const message = error instanceof Error ? error.message : "failed to read theme directory";
				diagnostics.push({ type: "warning", message, path });
			},
		});
		for (const entry of entries) {
			if (entry.isFile && entry.name.endsWith(".json")) {
				this.loadThemeFromFile(entry.path, themes, diagnostics);
			}
		}
	}

	private loadThemeFromFile(filePath: string, themes: Theme[], diagnostics: ResourceDiagnostic[]): void {
		try {
			themes.push(loadThemeFromPath(filePath));
		} catch (error) {
			const message = error instanceof Error ? error.message : "failed to load theme";
			diagnostics.push({ type: "warning", message, path: filePath });
		}
	}

	private async loadExtensionFactories(runtime: ExtensionRuntime): Promise<{
		extensions: Extension[];
		errors: Array<{ path: string; error: string }>;
	}> {
		const extensions: Extension[] = [];
		const errors: Array<{ path: string; error: string }> = [];

		for (const [index, factory] of this.extensionFactories.entries()) {
			const extensionPath = `<inline:${index + 1}>`;
			try {
				const extension = await loadExtensionFromFactory(factory, this.cwd, this.eventBus, runtime, extensionPath, {
					agentDir: this.agentDir,
				});
				extensions.push(extension);
			} catch (error) {
				const message = error instanceof Error ? error.message : "failed to load extension";
				errors.push({ path: extensionPath, error: message });
			}
		}

		return { extensions, errors };
	}

	private dedupePrompts(prompts: PromptTemplate[]): { prompts: PromptTemplate[]; diagnostics: ResourceDiagnostic[] } {
		const seen = new Map<string, PromptTemplate>();
		const diagnostics: ResourceDiagnostic[] = [];

		for (const prompt of prompts) {
			const existing = seen.get(prompt.name);
			if (existing) {
				diagnostics.push({
					type: "collision",
					message: `name "/${prompt.name}" collision`,
					path: prompt.filePath,
					collision: {
						resourceType: "prompt",
						name: prompt.name,
						winnerPath: existing.filePath,
						loserPath: prompt.filePath,
					},
				});
			} else {
				seen.set(prompt.name, prompt);
			}
		}

		return { prompts: Array.from(seen.values()), diagnostics };
	}

	private dedupeThemes(themes: Theme[]): { themes: Theme[]; diagnostics: ResourceDiagnostic[] } {
		const seen = new Map<string, Theme>();
		const diagnostics: ResourceDiagnostic[] = [];

		for (const t of themes) {
			const name = t.name ?? "unnamed";
			const existing = seen.get(name);
			if (existing) {
				diagnostics.push({
					type: "collision",
					message: `name "${name}" collision`,
					path: t.sourcePath,
					collision: {
						resourceType: "theme",
						name,
						winnerPath: existing.sourcePath ?? "<builtin>",
						loserPath: t.sourcePath ?? "<builtin>",
					},
				});
			} else {
				seen.set(name, t);
			}
		}

		return { themes: Array.from(seen.values()), diagnostics };
	}

	private discoverSystemPromptFile(): string | undefined {
		const projectPath = join(this.cwd, CONFIG_DIR_NAME, "SYSTEM.md");
		if (this.settingsManager.isProjectTrusted() && existsSync(projectPath)) {
			return projectPath;
		}

		const globalPath = configFile(this.agentDir, "SYSTEM.md");
		if (existsSync(globalPath)) {
			return globalPath;
		}

		return undefined;
	}

	private discoverAppendSystemPromptFile(): string | undefined {
		const projectPath = join(this.cwd, CONFIG_DIR_NAME, "APPEND_SYSTEM.md");
		if (this.settingsManager.isProjectTrusted() && existsSync(projectPath)) {
			return projectPath;
		}

		const globalPath = configFile(this.agentDir, "APPEND_SYSTEM.md");
		if (existsSync(globalPath)) {
			return globalPath;
		}

		return undefined;
	}

	private detectExtensionConflicts(extensions: Extension[]): Array<{ path: string; message: string }> {
		const conflicts: Array<{ path: string; message: string }> = [];

		// Track which extension registered each tool and flag
		const toolOwners = new Map<string, string>();
		const flagOwners = new Map<string, string>();

		for (const ext of extensions) {
			// Check tools
			for (const toolName of ext.tools.keys()) {
				const existingOwner = toolOwners.get(toolName);
				if (existingOwner && existingOwner !== ext.path) {
					conflicts.push({
						path: ext.path,
						message: `Tool "${toolName}" conflicts with ${existingOwner}`,
					});
				} else {
					toolOwners.set(toolName, ext.path);
				}
			}

			// Check flags
			for (const flagName of ext.flags.keys()) {
				const existingOwner = flagOwners.get(flagName);
				if (existingOwner && existingOwner !== ext.path) {
					conflicts.push({
						path: ext.path,
						message: `Flag "--${flagName}" conflicts with ${existingOwner}`,
					});
				} else {
					flagOwners.set(flagName, ext.path);
				}
			}
		}

		return conflicts;
	}
}
